const previewPortalOrigins = [
  'https://foh.obeeapp.com',
  'https://stagingfoh.obeeapp.com',
  'https://demofoh.obeeapp.com',
  'https://partners.eatclub.com.au',
  'https://stagingpp.eatclubhub.com',
  'https://demopp.eatclubhub.com',
];

export const getPreviewPortalOrigin = () => {
  if (window.parent === window) {
    return null;
  }

  try {
    const parentOrigin = new URL(document.referrer).origin;
    return previewPortalOrigins.includes(parentOrigin) ? parentOrigin : null;
  } catch {
    return null;
  }
};

export const isPreviewMode = () => {
  return (
    getPreviewPortalOrigin() !== null &&
    new URLSearchParams(window.location.search).get('preview') === 'true'
  );
};

const FALLBACK_TABLE = {
  id: 99,
  areaId: 1,
  tableNumber: '1',
  tableName: null,
  min: 1,
  max: 20,
  priority: 1,
};

const MOCK_SESSION_DEFINITIONS = [
  { label: 'Breakfast', startTime: 480, endTime: 600 },
  { label: 'Lunch', startTime: 660, endTime: 840 },
  { label: 'Dinner', startTime: 1020, endTime: 1410 },
];

// Returns sessions matching the real API shape: for each area, a pair of
// (default session with areaId:0, area-specific session with areaId:area.id).
export const getPreviewSessions = (areas) => {
  const safeAreas = areas && areas.length > 0 ? areas : [{ id: 1 }];
  return safeAreas.flatMap((area) =>
    MOCK_SESSION_DEFINITIONS.flatMap((s, i) => [
      {
        id: 100 + i,
        areaId: 0,
        label: s.label,
        startTime: s.startTime,
        endTime: s.endTime,
        isPublic: true,
        day: null,
        date: null,
        sittingTimes: [],
      },
      {
        id: 0,
        areaId: area.id,
        label: s.label,
        startTime: s.startTime,
        endTime: s.endTime,
        isPublic: true,
        day: null,
        date: null,
        sittingTimes: [],
      },
    ]),
  );
};

// Builds one mock table per real area, using each area's real id.
const buildTablesForAreas = (areas) => {
  if (!areas || areas.length === 0) return [FALLBACK_TABLE];
  return areas.map((area, i) => ({
    id: 99 + i,
    areaId: area.id,
    tableNumber: String(i + 1),
    tableName: null,
    min: 1,
    max: 20,
    priority: i + 1,
  }));
};

export const PREVIEW_MULTI_VENUE_REST_ID = '30113398-A294-471A-80BF-B57BA6676E75';

const MULTI_VENUE_TABLE_A = {
  id: 190,
  areaId: 17,
  tableNumber: '24',
  tableName: null,
  min: 1,
  max: 3,
  priority: 3,
};
const MULTI_VENUE_TABLE_B = {
  id: 159,
  areaId: 12,
  tableNumber: '6',
  tableName: null,
  min: 1,
  max: 4,
  priority: 13,
};
const MULTI_VENUE_TABLES = [MULTI_VENUE_TABLE_A, MULTI_VENUE_TABLE_B];

// Builds mock availability slots for the linked "Demo Multi Venue" in 15-min intervals.
export const buildPreviewMultiVenueSlots = () => {
  const slots = [];
  const seen = new Set();

  MOCK_SESSION_DEFINITIONS.forEach(({ startTime, endTime }) => {
    for (let t = startTime; t < endTime; t += 15) {
      if (!seen.has(t)) {
        seen.add(t);
        slots.push({
          restId: PREVIEW_MULTI_VENUE_REST_ID,
          time: t,
          table: MULTI_VENUE_TABLE_A,
          tables: MULTI_VENUE_TABLES,
          reason: null,
        });
      }
    }
  });

  return slots;
};

// Builds mock availability slots for Breakfast/Lunch/Dinner sessions.
// One slot per 30-min interval per session, with all area tables in each slot.
// restId must match restaurant.objectId so Time.js can look up the areas popover.
export const buildPreviewAvailabilitySlots = (areas, restId) => {
  const tables = buildTablesForAreas(areas);
  const sessions = getPreviewSessions(areas);

  const seen = new Set();
  const slots = [];

  sessions.forEach((session) => {
    const { startTime, endTime } = session;
    for (let t = startTime; t < endTime; t += 30) {
      if (!seen.has(t)) {
        seen.add(t);
        slots.push({
          restId: restId || null,
          time: t,
          table: tables[0],
          tables,
          reason: null,
          waitlist: false,
        });
      }
    }
  });

  return slots;
};

// Builds a mock CREATE_OBEE_BOOKING_SUCCESS payload using real booking data where
// available, so the confirmation screen shows the guest's actual details.
export const buildPreviewCreatedBooking = (booking) => {
  const guestEmail = booking?.guests?.email ?? null;
  const guestMobile = booking?.guests?.mobile ?? null;
  const guestMobilePrefix = booking?.guests?.mobilePrefix ?? null;

  return {
    data: {
      id: 2800,
      areaId: booking?.areaId ?? 1,
      date: booking?.date ?? null,
      time: booking?.time ?? null,
      size: booking?.size ?? 2,
      name: booking?.name ?? 'Preview Guest',
      customerComments: booking?.customerComments ?? null,
      status: 'booked',
      tableNumber: booking?.tableNumber ?? '3',
      duration: booking?.duration ?? 90,
      highchairs: booking?.highchairs ?? -1,
      kids: booking?.kids ?? -1,
      payments: '[]',
      paymentCustomerId: null,
      paymentMethodId: null,
      paymentMethodName: null,
      guests: [{ id: 0, email: guestEmail, mobile: guestMobile, mobilePrefix: guestMobilePrefix }],
      bookingCutOffs:
        '{"lastBookableSeconds":9999999999,"canEditOnline":true,"requireCancelReason":false,"canEditWithPayment":true,"lastOnlineCancellableSeconds":9999999999,"lastOnlineEditableSeconds":9999999999,"lastEditCancelDatetime":null,"lastEditDatetime":null}',
      requiresCC: false,
      addOns: '[]',
      experiences: '[]',
      purchasedExperiences: '[]',
      dietaryRequirements: null,
      vouchersRedeemed: null,
    },
  };
};

// A pay-later addon (includeInPayment: false) so the review page's order
// summary shows a "payment due later" amount, making settings like
// "Allow spending vouchers on addons that are paid for later" previewable
export const MOCK_PAY_LATER_ADDON = {
  id: 7,
  name: 'Cheese Board (pay at venue)',
  price: 18,
  description: 'Selection of local cheeses served to the table, paid for at the venue.',
  imageUrl: null,
  allImages: [],
  min: 0,
  max: 0,
  availableQuantity: 10,
  percentageOfFullPrice: 100,
  addOnMultiplier: 'per_group',
  extraContent: null,
  deleted: null,
  includeInPayment: false,
  paymentSettingsId: null,
  forceSingleQuantity: true,
  unit: null,
  showPrice: true,
};

const MOCK_EXPERIENCE = {
  id: 5,
  name: 'Dining Experience',
  displayName: 'Dining Experience',
  experienceTypeId: 6,
  experienceType: 'Dining',
  experienceTypeSettings: { event: 0, accommodation: 0, dining: 1, menu: 0 },
  price: 30,
  percentageOfFullPrice: 100,
  unit: null,
  primaryOptionId: 18,
  optionGroupId: 10,
  multiplier: 'per_group',
  optionalBehaviour: [
    'always_show',
    'allow_selection_when_unavailable',
    'dont_require_selection_foh',
  ],
  imageUrl: 'https://cdn.obeeapp.com/venue-assets/democlaudiav3/images/file1746156320498_1.png',
  allImages: [
    {
      imageId: 21,
      imageUrl: 'https://cdn.obeeapp.com/venue-assets/democlaudiav3/images/file1746156315422_0.png',
      main: false,
      active: true,
    },
    {
      imageId: 22,
      imageUrl: 'https://cdn.obeeapp.com/venue-assets/democlaudiav3/images/file1746156320498_1.png',
      main: true,
      active: true,
    },
  ],
  available: '10',
  quantityRestriction: null,
  showAvailableNumber: false,
  unavailableReasons: null,
  minGuests: 1,
  maxGuests: 20,
  selectableTimes: { timeOption: 'all', startTime: '00:00:00', endTime: '23:59:59' },
  selectableAreas: [],
  paymentSettingId: null,
  soldAlone: true,
  adultsOnly: false,
  selectionShowTotalPrice: true,
  selectionMultiplyPrice: true,
  customMessages: null,
  termsAndConditions: null,
  termsId: null,
  addonGroups: [
    {
      id: 2,
      experienceOptionsId: 134,
      name: 'Beverage Package',
      compulsory: false,
      headingLabel: null,
      matchGuests: false,
      showBeforeDetails: true,
      isAccommodation: false,
      showAccommodationStepPrice: false,
      isExclusiveSelect: false,
      forceSingleQuantity: false,
      active: true,
      priority: 1,
      addons: [
        {
          id: 3,
          name: 'House Wine (per person)',
          price: 25,
          description: 'Selection of red and white house wines.',
          imageUrl:
            'https://cdn.obeeapp.com/venue-assets/democlaudiav3/images/file1746082132900_0.png',
          allImages: [
            {
              imageId: 25,
              imageUrl:
                'https://cdn.obeeapp.com/venue-assets/democlaudiav3/images/file1746082132900_0.png',
              main: true,
              active: true,
            },
          ],
          min: 0,
          max: 0,
          availableQuantity: 10,
          percentageOfFullPrice: 100,
          addOnMultiplier: 'per_person',
          extraContent: null,
          deleted: null,
          includeInPayment: true,
          paymentSettingsId: null,
          forceSingleQuantity: false,
          unit: null,
          showPrice: true,
        },
        MOCK_PAY_LATER_ADDON,
      ],
    },
  ],
  description: 'A set dining experience crafted by our head chef, available for all group sizes.',
  fullDescription:
    'Enjoy a curated set menu featuring seasonal produce and matched wine suggestions. Suitable for groups of any size, with dietary requirements catered for on request.',
  showTermsSeparately: true,
  status: 'active',
};

const MOCK_DIETARY_TAGS = [
  { id: 1, default: 1, dietaryTag: 'Nut Allergy' },
  { id: 2, default: 1, dietaryTag: 'Seafood Allergy' },
  { id: 3, default: 1, dietaryTag: 'Vegan' },
  { id: 4, default: 1, dietaryTag: 'Lactose Free' },
  { id: 5, default: 1, dietaryTag: 'Gluten Free' },
  { id: 6, default: 1, dietaryTag: 'Vegetarian' },
  { id: 7, default: 1, dietaryTag: 'Dairy Free' },
  { id: 8, default: 1, dietaryTag: 'Pescatarian' },
];

const MOCK_BOOKING_TERMS_STRING = JSON.stringify({
  terms_exist: true,
  daily_view_display: 'Terms and Conditions',
  terms_conditions:
    'Reservations are confirmed upon receipt of a booking confirmation email. Please notify us of any dietary requirements or allergies at least 48 hours prior to your visit.<br /><br />Cancellations made with less than 24 hours notice may incur a cancellation fee. For groups of 8 or more, a minimum spend per person applies.',
});

const MOCK_PAYMENT_SETTINGS = {
  id: 7,
  date: null,
  day: null,
  time: null,
  size: null,
  source: null,
  mode: 'full',
  currency: 'AUD',
  title: 'Dining Experience',
  priority: 1,
  deleted: false,
  areaId: null,
  dateMax: null,
  timeOption: null,
  timeMax: null,
  sizeOption: 'less_than',
  sizeMax: 0,
  noShowAmount: null,
  noShowOption: null,
  depositAmount: null,
  depositOption: null,
  fullAmount: 60,
  fullOption: 'per_person',
  includeGST: true,
  shortDescription: 'Full payment is required at the time of booking.',
  policyUrl: null,
  policyText:
    'Payment is processed securely at the time of booking. Refunds are available for cancellations made more than 48 hours in advance.',
  staffPayRequired: 0,
  usedBy: 'bookings',
  pinMode: 'test',
  publishableKey: 'pk_hpC9sLgExkjQ2fbHmR_sRA',
  transactionFees: null,
};

export const PREVIEW_MOCK_DATA = {
  experiences: {
    data: {
      experiences: [MOCK_EXPERIENCE],
      forceAccommodationSelection: false,
      requireExperienceSelection: false,
    },
  },
  addons: {
    data: [],
  },
  heldBooking: {
    data: { id: 'preview-hold', areaId: null, tableNumber: '1' },
  },
  multiVenueRestaurant: {
    objectId: PREVIEW_MULTI_VENUE_REST_ID,
    name: 'Demo Multi Venue',
    address: '456 Preview Street, Melbourne VIC 3000',
    areas: [],
    premium: false,
    bookingWidgetSettings: JSON.stringify({
      themeSettings: {
        themeDetails: {
          venueDescription:
            'Another great venue in the group, available for walk-ins and bookings.',
        },
      },
    }),
  },
  // Static fallback used by confirmObeeBookingAction. For createObeeBookingAction,
  // use buildPreviewCreatedBooking(booking) instead so real guest data is shown.
  createdBooking: buildPreviewCreatedBooking(null),
  dietaryTags: {
    data: MOCK_DIETARY_TAGS,
  },
  bookingTerms: {
    data: MOCK_BOOKING_TERMS_STRING,
    meta: { variables: {} },
  },
  paymentSettings: {
    data: MOCK_PAYMENT_SETTINGS,
  },
};

export const PREVIEW_GUEST = {
  firstName: 'John',
  lastName: 'Doe',
  mobile: '0491000000',
  mobilePrefix: '+61',
  email: 'john.doe@example.com',
};

// 12:00pm, sits inside the mock Lunch session and its 30-min slot intervals
export const PREVIEW_TIME = 720;

// A valid redeemed voucher: covers the $30 due today with $10 left over, so
// the "Allow spending vouchers on addons that are paid for later" setting
// visibly changes the amount due later
export const MOCK_VOUCHER = {
  voucher: 'PREVIEW-40',
  valid: true,
  amount: 40,
  reason: null,
};

const getTodayDateString = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

// Booking state prefill so any step the portal navigates to renders fully
// populated (date, time, guest details, a selected experience) without the
// admin having to click through the widget.
export const buildPreviewBookingPrefill = (restaurantData) => {
  const areas = restaurantData?.areas?.length > 0 ? restaurantData.areas : [{ id: 1 }];
  const tables = buildTablesForAreas(areas);

  return {
    date: getTodayDateString(),
    guests: 2,
    areaId: areas[0].id,
    availabilitySuggestion: {
      restId: restaurantData?.objectId ?? null,
      time: PREVIEW_TIME,
      table: tables[0],
      preferredArea: null,
      // Marks the timebox with this time as selected without needing an exact table match
      isFromBooking: true,
    },
    ...PREVIEW_GUEST,
    postcode: '3000',
    specialRequirements: 'Celebrating a birthday, window seat if possible.',
    // Tag ids from MOCK_DIETARY_TAGS: 5 = Gluten Free, 3 = Vegan
    dietaryRequirements: [
      { name: 'John Doe', dietaries: [5], dietaryNotes: '', noDietaries: false, index: 0 },
      { name: 'Jane Doe', dietaries: [3], dietaryNotes: '', noDietaries: false, index: 1 },
    ],
    heldBookingId: PREVIEW_MOCK_DATA.heldBooking.data.id,
    selectedExperiences: [{ id: MOCK_EXPERIENCE.id, count: 1, experience: MOCK_EXPERIENCE }],
    voucherReferences: [MOCK_VOUCHER],
    // Preselect the pay-later addon so the order summary shows a "payment due
    // later" amount alongside the paid-today experience
    addons: {
      [MOCK_PAY_LATER_ADDON.id]: {
        addon: MOCK_PAY_LATER_ADDON,
        count: 1,
        compulsory: false,
        groupIndex: 0,
        addonGroupId: MOCK_EXPERIENCE.addonGroups[0].id,
      },
    },
  };
};

// Confirmed-booking payload for jumping straight to the confirmation step
export const buildPreviewConfirmedBooking = (restaurantData) => {
  const prefill = buildPreviewBookingPrefill(restaurantData);

  return buildPreviewCreatedBooking({
    areaId: prefill.areaId,
    date: prefill.date,
    time: PREVIEW_TIME,
    size: prefill.guests,
    name: `${PREVIEW_GUEST.firstName} ${PREVIEW_GUEST.lastName}`,
    guests: {
      email: PREVIEW_GUEST.email,
      mobile: PREVIEW_GUEST.mobile,
      mobilePrefix: PREVIEW_GUEST.mobilePrefix,
    },
  });
};
